import { API_URL } from "../config";
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, Link as LinkIcon, User as UserIcon, LogOut, Plus, Star, BarChart3, Eye, MousePointerClick, Unlock, AlertCircle, Send, Edit2, KeyRound, Crown, Paintbrush, Link2, ArrowRight, Trash2, CheckCircle2, Mail, ClipboardPaste, Copy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CreateLink from './CreateLink';
import CustomDropdown from '../components/CustomDropdown';
import { LoginButton } from '@telegram-auth/react';

const Dashboard = () => {
  
  const dustParticles = React.useMemo(() => {
    return Array.from({ length: 30 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1 + 'px',
      duration: Math.random() * 20 + 15,
      delay: Math.random() * 10,
    }));
  }, []);
  const { user, logout, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('home');

    const handleCopy = (linkId) => {
        const url = `${window.location.origin}/unlock/${linkId}`;
        navigator.clipboard.writeText(url).then(() => {
            alert('Link copied to clipboard!');
        }).catch(err => {
            console.error('Failed to copy: ', err);
            alert('Failed to copy link.');
        });
    };

  const [myLinks, setMyLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);

  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [profileMsg, setProfileMsg] = useState({ text: '', type: '' });

  const [isConnectingEmail, setIsConnectingEmail] = useState(false);
  const [connectEmail, setConnectEmail] = useState('');
  const [connectPassword, setConnectPassword] = useState('');
  const [connectOtp, setConnectOtp] = useState('');
  const [showConnectOtpInput, setShowConnectOtpInput] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showAccountCenterModal, setShowAccountCenterModal] = useState(false);
  const [accountCenterMsg, setAccountCenterMsg] = useState({ text: '', type: '' });

  const [selectedLinkForTheme, setSelectedLinkForTheme] = useState(null);
  const [themeForm, setThemeForm] = useState({ bgColor: '#0f172a', textColor: '#ffffff', buttonColor: '#3b82f6', animation: 'none', bgImage: null });
  const [bgImageFile, setBgImageFile] = useState(null);
  const [themeUpdating, setThemeUpdating] = useState(false);
  const [themeMsg, setThemeMsg] = useState({ text: '', type: '' });

  const [editingLink, setEditingLink] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', destinationUrl: '' });
  const [editMsg, setEditMsg] = useState({ text: '', type: '' });

  useEffect(() => {
    if (user) {
      const fetchMyLinks = async (isInitial = false) => {
        try {
          const response = await axios.get(`${API_URL}/users/me/links`);
          setMyLinks(response.data);
        } catch (err) {
          console.error("Error fetching links:", err);
        } finally {
          if (isInitial) setLoadingLinks(false);
        }
      };
      fetchMyLinks(true);

      const interval = setInterval(() => {
          if (document.visibilityState === 'visible') {
              fetchMyLinks(false);
          }
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    if (selectedLinkForTheme) {
        const link = myLinks.find(l => l.shortId === selectedLinkForTheme || l.id === selectedLinkForTheme);
        if (link && link.theme) {
            setThemeForm({
                bgColor: link.theme.bgColor || '#0f172a',
                textColor: link.theme.textColor || '#ffffff',
                buttonColor: link.theme.buttonColor || '#3b82f6',
                animation: link.theme.animation || 'none',
                bgImage: link.theme.bgImage || null
            });
        } else {
             setThemeForm({ bgColor: '#0f172a', textColor: '#ffffff', buttonColor: '#3b82f6', animation: 'none', bgImage: null });
        }
        setBgImageFile(null);
    }
  }, [selectedLinkForTheme, myLinks]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUpdateName = async () => {
      try {
          await axios.put(`${API_URL}/users/me`, { name: newName });
          await fetchUser();
          setIsEditingName(false);
          setProfileMsg({ text: 'Name updated successfully', type: 'success' });
      } catch (err) {
          setProfileMsg({ text: 'Failed to update name', type: 'error' });
      }
      setTimeout(() => setProfileMsg({ text: '', type: '' }), 3000);
  };

  const handleConnectEmailRequest = async (e) => {
      e.preventDefault();
      try {
          await axios.post(`${API_URL}/users/me/email/connect/request-otp`, { email: connectEmail });
          setShowConnectOtpInput(true);
          setAccountCenterMsg({ text: 'OTP sent to email', type: 'success' });
      } catch (err) {
          setAccountCenterMsg({ text: err.response?.data?.error || 'Failed to send OTP', type: 'error' });
      }
      setTimeout(() => setAccountCenterMsg({ text: '', type: '' }), 3000);
  };

  const handleConnectEmailVerify = async (e) => {
      e.preventDefault();
      try {
          await axios.post(`${API_URL}/users/me/email/connect/verify`, { email: connectEmail, password: connectPassword, otp: connectOtp });
          await fetchUser();
          setIsConnectingEmail(false);
          setConnectEmail('');
          setConnectPassword('');
          setConnectOtp('');
          setShowConnectOtpInput(false);
          setAccountCenterMsg({ text: 'Email connected successfully', type: 'success' });
      } catch (err) {
          setAccountCenterMsg({ text: err.response?.data?.error || 'Failed to connect email', type: 'error' });
      }
      setTimeout(() => setAccountCenterMsg({ text: '', type: '' }), 3000);
  };

  const handleConnectTelegram = async (telegramUser) => {
      try {
          await axios.post(`${API_URL}/users/me/telegram/connect`, telegramUser);
          await fetchUser();
          setAccountCenterMsg({ text: 'Telegram connected successfully', type: 'success' });
      } catch (err) {
          setAccountCenterMsg({ text: err.response?.data?.error || 'Failed to connect Telegram', type: 'error' });
      }
      setTimeout(() => setAccountCenterMsg({ text: '', type: '' }), 3000);
  };

  const handleChangePassword = async (e) => {
      e.preventDefault();
      try {
          await axios.put(`${API_URL}/users/me/password`, { currentPassword, newPassword });
          setIsChangingPassword(false);
          setCurrentPassword('');
          setNewPassword('');
          setAccountCenterMsg({ text: 'Password updated successfully', type: 'success' });
      } catch (err) {
          setAccountCenterMsg({ text: err.response?.data?.error || 'Failed to update password', type: 'error' });
      }
      setTimeout(() => setAccountCenterMsg({ text: '', type: '' }), 3000);
  };

  const handleDisconnectTelegram = async () => {
      try {
          await axios.post(`${API_URL}/users/me/telegram/disconnect`);
          setShowDisconnectModal(false);
          logout();
          navigate('/');
      } catch (err) {
          setShowDisconnectModal(false);
          setProfileMsg({ text: 'Failed to disconnect and delete account', type: 'error' });
          setTimeout(() => setProfileMsg({ text: '', type: '' }), 3000);
      }
  };

  if (!user) return null;

  const currentPlan = user?.plan || 'free';
  const isFreePlan = currentPlan === 'free';
  const isBasicPlan = currentPlan === 'basic';
  const maxLinks = isBasicPlan ? 5 : (isFreePlan ? 2 : Infinity);
  const canCreateLink = myLinks.length < maxLinks;

  const navItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'links', label: 'Links', icon: LinkIcon },
    { id: 'theme', label: 'Theme', icon: Paintbrush },
    { id: 'profile', label: 'Profile', icon: UserIcon },
  ];

  const handleEditLink = async (e) => {
    e.preventDefault();
    if (!editingLink) return;
    try {
        await axios.put(`${API_URL}/links/${editingLink.id}`, editForm);
        setMyLinks(myLinks.map(l => l.id === editingLink.id ? { ...l, title: editForm.title, destinationUrl: editForm.destinationUrl } : l));
        setEditMsg({ text: 'Link updated successfully', type: 'success' });
        setTimeout(() => setEditingLink(null), 1000);
    } catch (err) {
        setEditMsg({ text: err.response?.data?.error || 'Failed to update link', type: 'error' });
    }
    setTimeout(() => setEditMsg({ text: '', type: '' }), 3000);
  };

  const handleUpdateTheme = async (e) => {
    e.preventDefault();
    if (!selectedLinkForTheme) return;
    setThemeUpdating(true);
    try {
        const formData = new FormData();
        formData.append('bgColor', themeForm.bgColor);
        formData.append('textColor', themeForm.textColor);
        formData.append('buttonColor', themeForm.buttonColor);
        formData.append('animation', themeForm.animation);
        if (bgImageFile) {
            formData.append('bgImage', bgImageFile);
        } else if (themeForm.bgImage === null) {
            formData.append('removeBgImage', 'true');
        }

        await axios.put(`${API_URL}/links/${selectedLinkForTheme}/theme`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        setThemeMsg({ text: 'Theme updated successfully!', type: 'success' });
        setBgImageFile(null);
        
        const response = await axios.get(`${API_URL}/users/me/links`);
        setMyLinks(response.data);
    } catch (err) {
        setThemeMsg({ text: err.response?.data?.error || 'Failed to update theme', type: 'error' });
    } finally {
        setThemeUpdating(false);
        setTimeout(() => setThemeMsg({ text: '', type: '' }), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-black text-gray-100 font-sans md:flex selection:bg-purple-500/30 overflow-hidden relative">
        {}
        <div className="fixed inset-0 z-0 pointer-events-none flex justify-center items-end overflow-hidden mix-blend-screen opacity-40">
            <motion.div 
                className="absolute bottom-0 w-[40vw] h-[80vh] origin-bottom filter blur-[60px]"
                style={{
                    background: 'linear-gradient(to top, rgba(147, 51, 234, 0.4) 0%, transparent 100%)',
                    transformPerspective: '1000px',
                    rotateX: '45deg',
                }}
                animate={{
                    opacity: [0.3, 0.6, 0.3],
                    scale: [1, 1.05, 1],
                }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div 
                className="absolute bottom-0 w-[60vw] h-[60vh] origin-bottom filter blur-[80px]"
                style={{
                    background: 'linear-gradient(to top, rgba(168, 85, 247, 0.3) 0%, transparent 100%)',
                    transformPerspective: '1000px',
                    rotateX: '45deg',
                }}
                animate={{
                    opacity: [0.2, 0.5, 0.2],
                    scale: [1, 1.1, 1],
                }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            />
        </div>

        {}
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden mix-blend-screen">
            {dustParticles.map((particle) => (
                <motion.div
                    key={`dust-${particle.id}`}
                    className="absolute rounded-full bg-violet-200 shadow-[0_0_8px_2px_rgba(168,85,247,0.5)]"
                    style={{
                        width: particle.size,
                        height: particle.size,
                        left: `${particle.x}%`,
                        bottom: `${particle.y}%`,
                    }}
                    animate={{
                        y: [0, -100, -300],
                        x: [0, Math.random() * 60 - 30, Math.random() * 100 - 50],
                        opacity: [0, 0.7, 0],
                    }}
                    transition={{
                        duration: particle.duration,
                        repeat: Infinity,
                        ease: "linear",
                        delay: particle.delay,
                    }}
                />
            ))}
        </div>

      {}
      <aside className="hidden md:flex flex-col w-64 bg-transparent backdrop-blur-xl border-r border-white/10 fixed h-full z-20 shadow-2xl">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8 px-2">
            <div className="flex items-center gap-2 text-white">
               <KeyRound className="w-6 h-6 -rotate-45 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
            </div>
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400 tracking-tight">Linkey</span>
          </div>

          <nav className="space-y-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 relative group ${
                  activeTab === item.id 
                    ? 'text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {activeTab === item.id && (
                  <motion.div
                    layoutId="sidebar-active-bg"
                    className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-xl -z-10"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <item.icon className={`h-5 w-5 transition-colors duration-300 ${activeTab === item.id ? 'text-blue-400' : 'group-hover:text-blue-400'}`} />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>
        </div>
        
        <div className="mt-auto p-6">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4">
             <div className="flex items-center gap-3 mb-3">
               {user.photoUrl ? (
                  <img src={user.photoUrl} alt="Profile" className="h-10 w-10 rounded-full border-2 border-slate-700" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-slate-700 flex items-center justify-center">
                    <UserIcon className="h-5 w-5 text-slate-400" />
                  </div>
                )}
                <div className="overflow-hidden">
                  <p className="text-sm font-semibold text-white truncate">{user.name || 'User'}</p>
                  <p className="text-xs text-slate-400 truncate capitalize">{user.plan && user.plan !== 'free' ? user.plan.replace('_', ' ') + ' Plan' : 'Free Plan'}</p>
                </div>
             </div>
          </div>
          <button 
            onClick={handleLogout} 
            className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all duration-300 group"
          >
            <LogOut className="h-5 w-5 group-hover:scale-110 transition-transform" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 md:ml-64 pb-24 md:pb-0 min-h-screen relative z-10">
      {}
      <div className="md:hidden bg-transparent backdrop-blur-md px-4 py-3 flex justify-between items-center sticky top-0 z-20 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-white">
             <KeyRound className="w-5 h-5 -rotate-45 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
          </div>
          <span className="font-bold text-lg tracking-tight">Linkey</span>
        </div>

        <div className="flex items-center gap-2 bg-white/5 rounded-full pl-3 pr-1 py-1 border border-white/10">
          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
          <span className="text-sm font-bold mr-2">0</span>
          <button onClick={() => setActiveTab('links')} className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 rounded-full p-1.5 text-white transition-all shadow-md">
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {}
      <div className="hidden md:flex px-8 py-6 justify-between items-center sticky top-0 z-20 bg-transparent backdrop-blur-md border-b border-white/10">
        <div>
          <h2 className="text-2xl font-bold text-white capitalize">{activeTab}</h2>
          <p className="text-sm text-slate-400">Welcome back, {user.name?.split(' ')[0] || 'User'}!</p>
        </div>
        <div className="flex items-center gap-4">
           <div className="flex items-center gap-2 bg-slate-800/80 rounded-full px-4 py-2 border border-slate-700 shadow-sm cursor-pointer hover:bg-slate-800 transition-colors">
            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
            <span className="text-sm font-bold">0 Credits</span>
          </div>
          <button onClick={() => setActiveTab('links')} className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-medium px-5 py-2 rounded-full transition-all shadow-lg shadow-blue-500/20 flex items-center gap-2 group">
            <Plus className="h-4 w-4 group-hover:rotate-90 transition-transform" />
            New Link
          </button>
        </div>
      </div>

      {}
          <div className="p-4 md:p-8 relative z-10">

        <div className="md:max-w-7xl md:mx-auto space-y-6">
        
        {}
        <AnimatePresence>
            {(isFreePlan || isBasicPlan || !user.telegramId) && (
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                className="grid gap-4 md:grid-cols-2"
              >
                {}
                {(isFreePlan || isBasicPlan) && (
                    <div className="bg-gradient-to-r from-fuchsia-900/20 to-purple-900/20 border border-fuchsia-500/30 rounded-2xl p-4 flex items-center justify-between shadow-lg shadow-purple-500/5 backdrop-blur-sm">
                        <div className="flex items-center gap-3">
                            <div className="bg-fuchsia-500/20 p-2 rounded-lg">
                               <Star className="h-5 w-5 text-fuchsia-400" />
                            </div>
                            <div className="text-sm">
                                <span className="font-bold text-fuchsia-100 block capitalize">{currentPlan} Plan Limited</span>
                                <span className="text-xs text-fuchsia-300/80">{maxLinks} link{maxLinks > 1 ? 's' : ''} allowed. Upgrade for unlimited.</span>
                            </div>
                        </div>
                        <button onClick={() => navigate('/pricing')} className="bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 text-white text-xs font-bold py-2 px-4 rounded-xl transition-all shadow-md hover:shadow-fuchsia-500/25 active:scale-95">
                            Upgrade Now
                        </button>
                    </div>
                )}

                {}
                {!user.telegramId && (
                     <div className="bg-blue-900/20 border border-blue-500/30 rounded-2xl p-4 flex items-center justify-between shadow-lg shadow-blue-500/5 backdrop-blur-sm">
                        <div className="flex items-center gap-3">
                            <div className="bg-blue-500/20 p-2 rounded-lg">
                                <Send className="h-5 w-5 text-blue-400" />
                            </div>
                            <div className="text-sm">
                                <span className="font-bold text-blue-100 block">Connect Telegram</span>
                                <span className="text-xs text-blue-300/80">Get fast login & alerts.</span>
                            </div>
                        </div>
                        <div className="flex justify-center items-center py-1 px-4 bg-blue-600 hover:bg-blue-500 rounded-xl transition-all shadow-md hover:shadow-blue-500/25 active:scale-95 overflow-hidden scale-75 origin-right h-[32px]">
                            <LoginButton botUsername={process.env.REACT_APP_TELEGRAM_BOT_NAME || "OfficialLinkeyBot"} onAuthCallback={handleConnectTelegram} buttonSize="small" />
                        </div>
                    </div>
                )}
              </motion.div>
            )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
        {activeTab === 'home' && (
          <motion.div 
            key="home"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="space-y-6 md:grid md:grid-cols-12 md:gap-6 md:space-y-0"
          >
             <motion.div 
                 initial={{ scale: 0.95, opacity: 0 }}
                 animate={{ scale: 1, opacity: 1 }}
                 transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 20 }}
                 className="md:col-span-4 bg-gradient-to-br from-indigo-900/40 via-[#0f172a] to-black rounded-3xl p-8 text-center shadow-2xl relative overflow-hidden h-fit border border-white/10 group"
             >
                 {}
                 <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                 <div className="absolute top-0 right-0 -mt-16 -mr-16 h-48 w-48 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none"></div>
                 <div className="absolute bottom-0 left-0 -mb-16 -ml-16 h-48 w-48 rounded-full bg-blue-500/10 blur-3xl pointer-events-none"></div>
                 
                 <div className="relative z-10 flex flex-col items-center">
                     <div className="relative mb-6">
                         <div className="absolute inset-0 bg-indigo-500 blur-xl opacity-30 rounded-full animate-pulse"></div>
                         <div className="bg-gradient-to-br from-indigo-500/20 to-blue-500/20 p-5 rounded-3xl border border-white/10 backdrop-blur-md relative z-10 shadow-inner">
                            <Plus className="h-10 w-10 text-indigo-300" />
                         </div>
                     </div>
                     <h2 className="text-2xl font-bold tracking-tight mb-3 text-white">
                         Create New Link
                     </h2>
                     <p className="text-sm text-slate-400 mb-8 font-medium leading-relaxed max-w-[250px] mx-auto">
                         Build powerful gateway links to protect your content and grow your audience seamlessly.
                     </p>
                     <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setActiveTab('links')}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold tracking-wide py-3.5 px-8 rounded-xl shadow-[0_0_20px_rgba(79,70,229,0.3)] hover:shadow-[0_0_30px_rgba(79,70,229,0.5)] w-full flex items-center justify-center gap-2 transition-all border border-indigo-400/30 relative overflow-hidden group/btn mb-4"
                     >
                         <span className="absolute inset-0 w-full h-full -mt-1 rounded-lg opacity-30 bg-gradient-to-b from-transparent via-transparent to-black pointer-events-none"></span>
                         <span className="relative z-10 flex items-center gap-2"><Plus className="h-5 w-5" /> Start Building</span>
                     </motion.button>
                     <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setActiveTab('manage-links')}
                        className="bg-white/5 hover:bg-white/10 text-white font-bold tracking-wide py-3 px-8 rounded-xl w-full flex items-center justify-center gap-2 transition-all border border-white/10 relative overflow-hidden group/btn"
                     >
                         <span className="relative z-10 flex items-center gap-2"><LinkIcon className="h-5 w-5" /> Manage Links</span>
                     </motion.button>
                 </div>
             </motion.div>

             <div className="md:col-span-8 bg-gradient-to-b from-[#0f172a]/80 to-black/80 backdrop-blur-xl rounded-3xl p-6 shadow-xl border border-white/10 relative overflow-hidden">
                 <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                 
                 <div className="flex items-center justify-between mb-8 pb-4 border-b border-white/5">
                     <div className="flex items-center gap-4">
                         <div className="p-3 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-white/5 shadow-inner">
                             <BarChart3 className="h-6 w-6 text-indigo-400" />
                         </div>
                         <div>
                             <h3 className="text-xl font-bold text-white tracking-tight">Overview</h3>
                             <p className="text-xs text-slate-400 font-medium">Your recent performance</p>
                         </div>
                     </div>
                 </div>

                 {loadingLinks ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="relative">
                            <div className="h-12 w-12 rounded-full border-4 border-slate-800 border-t-indigo-500 animate-spin"></div>
                            <div className="absolute inset-0 h-12 w-12 rounded-full border-4 border-transparent border-b-blue-500 animate-[spin_1.5s_linear_infinite_reverse]"></div>
                        </div>
                    </div>
                 ) : myLinks.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                        <div className="h-20 w-20 bg-slate-800/50 rounded-3xl flex items-center justify-center border border-white/5 mb-6 shadow-inner relative">
                            <div className="absolute inset-0 bg-indigo-500/10 blur-xl rounded-3xl"></div>
                            <LinkIcon className="h-10 w-10 text-slate-600 relative z-10" />
                        </div>
                        <h4 className="text-lg font-bold text-slate-200 mb-2">No Links Active</h4>
                        <p className="text-sm text-slate-500 max-w-[280px]">Your workspace is empty. Create a new link gateway to start tracking analytics.</p>
                    </div>
                 ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {myLinks.slice(0, 4).map((link, i) => (
                            <motion.div 
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.05 + 0.2 }}
                                key={link.id} 
                                className="bg-[#0b1121] p-5 rounded-2xl border border-white/5 hover:border-indigo-500/30 transition-all duration-300 flex flex-col group relative overflow-hidden"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                                
                                <div className="flex justify-between items-start mb-5 relative z-10">
                                    <div className="flex-1 min-w-0 pr-4">
                                        <h4 className="font-bold text-slate-100 truncate text-base mb-1" title={link.title}>{link.title}</h4>
                                        <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                                            <span className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span> Active
                                        </div>
                                    </div>
                                    <a href={`/unlock/${link.id}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 hover:text-white transition-colors border border-white/5 shrink-0">
                                        <ArrowRight className="h-4 w-4 -rotate-45" />
                                    </a>
                                </div>
                                
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 relative z-10 mt-auto">
                                    <div className="bg-[#0f172a] rounded-xl p-2.5 flex flex-col border border-white/5 overflow-hidden">
                                        <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 whitespace-nowrap"><Eye className="h-3 w-3 text-slate-400 shrink-0" /> Visits</span>
                                        <span className="text-lg font-bold text-slate-200 truncate">{link.stats?.visits || 0}</span>
                                    </div>
                                    <div className="bg-[#0f172a] rounded-xl p-2.5 flex flex-col border border-white/5 overflow-hidden">
                                        <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 whitespace-nowrap"><Star className="h-3 w-3 text-yellow-400 shrink-0" /> Unique</span>
                                        <span className="text-lg font-bold text-slate-200 truncate">{link.stats?.uniqueViews || 0}</span>
                                    </div>
                                    <div className="bg-[#0f172a] rounded-xl p-2.5 flex flex-col border border-white/5 overflow-hidden">
                                        <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 whitespace-nowrap"><MousePointerClick className="h-3 w-3 text-indigo-400 shrink-0" /> Clicks</span>
                                        <span className="text-lg font-bold text-slate-200 truncate">{link.stats?.clicks || 0}</span>
                                    </div>
                                    <div className="bg-[#0f172a] rounded-xl p-2.5 flex flex-col border border-white/5 overflow-hidden">
                                        <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 whitespace-nowrap"><Unlock className="h-3 w-3 text-emerald-400 shrink-0" /> Unlocks</span>
                                        <span className="text-lg font-bold text-slate-200 truncate">{link.stats?.unlocks || 0}</span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                 )}
             </div>
          </motion.div>
        )}

        {activeTab === 'links' && (
          <motion.div 
            key="links"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="max-w-4xl mx-auto"
          >
            {!canCreateLink && !editingLink ? (
                <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-10 text-center shadow-xl border border-fuchsia-500/30 mt-4 relative overflow-hidden">
                    <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-fuchsia-500 to-purple-600"></div>
                    <div className="bg-fuchsia-500/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <AlertCircle className="h-10 w-10 text-fuchsia-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-3">Limit Reached</h2>
                    <p className="text-slate-400 mb-8 max-w-md mx-auto">
                        You have reached the limit of 1 link on the Free Plan. Upgrade your account to create unlimited custom gateways.
                    </p>
                    <button className="bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 text-white font-bold py-4 px-8 rounded-2xl shadow-lg hover:shadow-fuchsia-500/25 transition-all active:scale-95 w-full md:w-auto">
                        Upgrade to Premium
                    </button>
                </div>
            ) : (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                            <div className="p-2 bg-indigo-500/20 rounded-xl">
                                <LinkIcon className="h-6 w-6 text-indigo-400" />
                            </div>
                            Create Gateway
                        </h2>
                    </div>
                    {}
                    <div className="bg-transparent overflow-hidden">
                        {editingLink ? (
                            <><CreateLink initialData={editingLink} onSuccess={() => {
                                const fetchLinks = async () => {
                                    try {
                                        const response = await axios.get(`${API_URL}/users/me/links`);
                                        setMyLinks(response.data);
                                    } catch (err) {}
                                };
                                fetchLinks();
                                setEditingLink(null);
                                setActiveTab('manage-links');
                            }} />
                            <div className="mt-4 text-center"><button onClick={() => setEditingLink(null)} className="text-sm text-slate-400 hover:text-white">Cancel Editing</button></div></>
                        ) : (
                            <>
                                <div className="flex justify-end mb-4"><button onClick={() => setActiveTab('manage-links')} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors text-sm font-bold"><LinkIcon className="h-4 w-4"/> Manage Links</button></div>
                                <CreateLink onSuccess={() => {
                                const fetchLinks = async () => {
                                    try {
                                        const response = await axios.get(`${API_URL}/users/me/links`);
                                        setMyLinks(response.data);
                                    } catch (err) {}
                                };
                                fetchLinks();
                            }} />
                            </>
                        )}
                    </div>
                </div>
            )}
          </motion.div>
        )}

        {activeTab === 'manage-links' && (
          <motion.div 
            key="manage-links"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="max-w-4xl mx-auto"
          >
                    {}
                    <div className="mt-6">
                        <h2 className="text-2xl font-bold text-white mb-6 border-b border-white/10 pb-4 flex items-center gap-3"><LinkIcon className="h-6 w-6 text-indigo-400" /> Manage Links</h2>
                        {myLinks.length === 0 ? (
                            <p className="text-slate-400 text-center py-4 bg-white/5 rounded-xl border border-white/5">No links created yet.</p>
                        ) : (
                            <div className="grid grid-cols-1 gap-4">
                                {myLinks.map((link) => (
                                    <div key={`manage-${link.id}`} className="bg-[#0b1121] p-5 rounded-2xl border border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="flex-1 min-w-0 pr-4">
                                            <h4 className="font-bold text-slate-100 truncate text-lg mb-1">{link.title}</h4>
                                            <a href={`/unlock/${link.id}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:text-blue-300 truncate block">
                                                {`${window.location.origin}/unlock/${link.id}`}
                                            </a>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 rounded-lg border border-white/10 mr-2">
                                                <Eye className="h-4 w-4 text-slate-400" />
                                                <span className="text-sm font-bold text-slate-200">{link.stats?.visits || 0}</span>
                                            </div>
                                            <button 
        onClick={() => handleCopy(link.id)}
        className="p-2.5 bg-emerald-900/30 hover:bg-emerald-500/20 rounded-lg text-emerald-400 hover:text-emerald-300 transition-colors border border-emerald-500/20 mr-1"
        title="Copy Link"
    >
        <Copy className="h-4 w-4" />
    </button>
                                            <button
                                                onClick={() => {
                                                    setEditingLink(link);
                                                    setEditForm({ title: link.title || '', destinationUrl: link.destinationUrl || '' });
                                                    setActiveTab('links');
                                                }}
                                                className="p-2.5 bg-blue-900/30 hover:bg-blue-500/20 rounded-lg text-blue-400 hover:text-blue-300 transition-colors border border-blue-500/20"
                                                title="Edit Link"
                                            >
                                                <Edit2 className="h-4 w-4" />
                                            </button>
                                            <button 
                                                onClick={async () => {
                                                    if (window.confirm("Are you sure you want to delete this link?")) {
                                                        try {
                                                            await axios.delete(`${API_URL}/links/${link.id}`);
                                                            setMyLinks(myLinks.filter(l => l.id !== link.id));
                                                        } catch (err) {
                                                            console.error("Failed to delete link", err);
                                                            alert("Failed to delete link.");
                                                        }
                                                    }
                                                }}
                                                className="p-2.5 bg-red-900/30 hover:bg-red-500/20 rounded-lg text-red-400 hover:text-red-300 transition-colors border border-red-500/20"
                                                title="Delete Link"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
          </motion.div>
        )}

        {}
        {activeTab === 'theme' && (
          <motion.div
            key="theme"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="p-4 md:p-8 max-w-5xl mx-auto"
          >
             <div className="mb-6 md:mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-3">
                    <div className="p-2 md:p-3 bg-pink-500/20 rounded-xl md:rounded-2xl border border-pink-500/30">
                        <Paintbrush className="h-6 w-6 md:h-7 md:w-7 text-pink-400" />
                    </div>
                    Gateway Theme
                </h2>
                <p className="text-slate-400 text-sm md:text-base mt-2">Design a unique, premium experience for your locked links.</p>
             </div>

             <div className="bg-white/[0.02] backdrop-blur-2xl border border-white/5 rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-2xl relative overflow-hidden">
                {}
                <div className="absolute top-0 right-0 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl -z-10 transform translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -z-10 transform -translate-x-1/2 translate-y-1/2"></div>
                
                <div className="mb-8 relative z-[60]">
                    <label className="block text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wider">Select Gateway to Customize</label>
                    <CustomDropdown
                        options={myLinks.map(link => ({
                            value: link.shortId || link.id,
                            label: (
                                <div className="flex items-center justify-between w-full pr-2">
                                    <span className="font-semibold text-white">{link.title}</span>
                                    <span className="text-xs bg-white/10 px-2 py-0.5 rounded-md text-slate-300 font-mono border border-white/5">{link.shortId}</span>
                                </div>
                            ),
                            plainLabel: `${link.title} (${link.shortId})` 
                        }))}
                        value={selectedLinkForTheme || ''}
                        onChange={(value) => setSelectedLinkForTheme(value)}
                        placeholder="-- Choose a gateway to customize --"
                        className="bg-black/40 border-2 border-white/10 py-4 px-5 text-white font-medium hover:bg-black/60 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 shadow-[0_0_20px_rgba(0,0,0,0.5)] rounded-2xl backdrop-blur-xl"
                        dropdownClassName="bg-slate-900/95 border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.8)] backdrop-blur-2xl rounded-2xl mt-3 p-1"
                        icon={Link2}
                        renderOption={(option, isSelected) => (
                            <div className={`w-full flex items-center justify-between ${isSelected ? 'text-blue-400' : 'text-gray-200'}`}>
                               {option.label}
                            </div>
                        )}
                    />
                </div>

                {selectedLinkForTheme ? (
                    <form onSubmit={handleUpdateTheme} className="space-y-8 relative z-10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                            <div className="space-y-8">
                                <div className="bg-black/20 p-5 md:p-6 rounded-2xl border border-white/5 space-y-6">
                                    <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2 border-b border-white/5 pb-3">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                        Color Palette
                                    </h3>
                                    
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        {}
                                        <div className="group relative">
                                            <label className="block text-xs font-semibold text-slate-400 mb-2 group-hover:text-slate-300 transition-colors">Background Color</label>
                                            <div className="flex items-center gap-3 bg-black/40 p-2 rounded-xl border border-white/10 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all">
                                                <div className="relative w-10 h-10 rounded-lg overflow-hidden shrink-0 border border-white/20 shadow-inner cursor-pointer hover:scale-105 transition-transform">
                                                    <input 
                                                        type="color" 
                                                        value={themeForm.bgColor} 
                                                        onChange={(e) => setThemeForm({...themeForm, bgColor: e.target.value})} 
                                                        className="absolute inset-[-10px] w-[200%] h-[200%] cursor-pointer p-0 m-0 border-0 outline-none" 
                                                    />
                                                </div>
                                                <input 
                                                    type="text" 
                                                    value={themeForm.bgColor} 
                                                    onChange={(e) => setThemeForm({...themeForm, bgColor: e.target.value})} 
                                                    className="w-full bg-transparent border-none text-sm text-gray-200 uppercase font-mono focus:outline-none focus:ring-0" 
                                                    placeholder="#0F172A"
                                                />
                                            </div>
                                        </div>

                                        {}
                                        <div className="group relative">
                                            <label className="block text-xs font-semibold text-slate-400 mb-2 group-hover:text-slate-300 transition-colors">Text Color</label>
                                            <div className="flex items-center gap-3 bg-black/40 p-2 rounded-xl border border-white/10 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all">
                                                <div className="relative w-10 h-10 rounded-lg overflow-hidden shrink-0 border border-white/20 shadow-inner cursor-pointer hover:scale-105 transition-transform">
                                                    <input 
                                                        type="color" 
                                                        value={themeForm.textColor} 
                                                        onChange={(e) => setThemeForm({...themeForm, textColor: e.target.value})} 
                                                        className="absolute inset-[-10px] w-[200%] h-[200%] cursor-pointer p-0 m-0 border-0 outline-none" 
                                                    />
                                                </div>
                                                <input 
                                                    type="text" 
                                                    value={themeForm.textColor} 
                                                    onChange={(e) => setThemeForm({...themeForm, textColor: e.target.value})} 
                                                    className="w-full bg-transparent border-none text-sm text-gray-200 uppercase font-mono focus:outline-none focus:ring-0" 
                                                />
                                            </div>
                                        </div>

                                        {}
                                        <div className="group relative sm:col-span-2">
                                            <label className="block text-xs font-semibold text-slate-400 mb-2 group-hover:text-slate-300 transition-colors">Primary Button Color</label>
                                            <div className="flex items-center gap-3 bg-black/40 p-2 rounded-xl border border-white/10 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all">
                                                <div className="relative w-10 h-10 rounded-lg overflow-hidden shrink-0 border border-white/20 shadow-inner cursor-pointer hover:scale-105 transition-transform">
                                                    <input 
                                                        type="color" 
                                                        value={themeForm.buttonColor} 
                                                        onChange={(e) => setThemeForm({...themeForm, buttonColor: e.target.value})} 
                                                        className="absolute inset-[-10px] w-[200%] h-[200%] cursor-pointer p-0 m-0 border-0 outline-none" 
                                                    />
                                                </div>
                                                <input 
                                                    type="text" 
                                                    value={themeForm.buttonColor} 
                                                    onChange={(e) => setThemeForm({...themeForm, buttonColor: e.target.value})} 
                                                    className="w-full bg-transparent border-none text-sm text-gray-200 uppercase font-mono focus:outline-none focus:ring-0" 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-black/20 p-5 md:p-6 rounded-2xl border border-white/5 space-y-6">
                                    <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2 border-b border-white/5 pb-3">
                                        <div className="w-1.5 h-1.5 rounded-full bg-pink-500"></div>
                                        Effects & Media
                                    </h3>
                                    
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-400 mb-3">Button Animation Preset</label>
                                        <div className="grid grid-cols-3 gap-3">
                                            {['none', 'pulse', 'bounce'].map(anim => (
                                                <button
                                                    key={anim}
                                                    type="button"
                                                    onClick={() => setThemeForm({...themeForm, animation: anim})}
                                                    className={`relative overflow-hidden px-2 py-3 rounded-xl text-xs font-bold capitalize transition-all duration-300 border-2 ${
                                                        themeForm.animation === anim 
                                                            ? 'bg-blue-600/20 border-blue-500 text-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.3)]' 
                                                            : 'bg-black/40 border-white/5 text-slate-400 hover:bg-white/5 hover:text-slate-200 hover:border-white/20'
                                                    }`}
                                                >
                                                    {themeForm.animation === anim && (
                                                        <motion.div layoutId="animIndicator" className="absolute inset-0 bg-blue-500/10 rounded-xl" />
                                                    )}
                                                    <span className="relative z-10 flex items-center justify-center gap-2">
                                                        {anim === 'pulse' && <span className="w-2 h-2 rounded-full bg-current animate-pulse"></span>}
                                                        {anim === 'bounce' && <span className="w-2 h-2 rounded-full bg-current animate-bounce"></span>}
                                                        {anim === 'none' && <span className="w-2 h-2 rounded-full bg-current opacity-50"></span>}
                                                        {anim}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-400 mb-3">Background Image</label>
                                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                                            <div className="relative flex-1 group">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    id="bg-image-upload"
                                                    className="sr-only"
                                                    onChange={(e) => {
                                                        if (e.target.files && e.target.files[0]) {
                                                            setBgImageFile(e.target.files[0]);
                                                            const fileReader = new FileReader();
                                                            fileReader.onload = (event) => {
                                                                setThemeForm({...themeForm, bgImage: event.target.result});
                                                            };
                                                            fileReader.readAsDataURL(e.target.files[0]);
                                                        }
                                                    }}
                                                />
                                                <label 
                                                    htmlFor="bg-image-upload"
                                                    className="flex flex-col items-center justify-center w-full px-4 py-4 border-2 border-dashed border-white/20 group-hover:border-blue-500 group-hover:bg-blue-500/5 rounded-xl cursor-pointer bg-black/40 transition-all text-sm text-slate-300 font-medium"
                                                >
                                                    <span className="mb-1 text-slate-400">{bgImageFile ? 'Change Image' : 'Click to upload image'}</span>
                                                    <span className="text-xs text-slate-500 font-normal">JPG, PNG, WEBP (Max 5MB)</span>
                                                </label>
                                            </div>
                                            {themeForm.bgImage && (
                                                <button 
                                                    type="button"
                                                    onClick={() => {
                                                        setBgImageFile(null);
                                                        setThemeForm({...themeForm, bgImage: null});
                                                    }}
                                                    className="flex items-center justify-center gap-2 text-xs font-bold text-red-400 hover:text-red-300 px-4 py-4 bg-red-500/10 hover:bg-red-500/20 rounded-xl shrink-0 transition-colors border border-red-500/20"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {}
                            <div className="flex flex-col h-full">
                                <h3 className="text-sm font-bold text-slate-300 mb-4 uppercase tracking-wider flex items-center gap-2">
                                    <Eye className="h-4 w-4 text-slate-400"/> Live Preview
                                </h3>
                                <div 
                                    className="relative rounded-2xl md:rounded-3xl border border-white/10 flex-1 flex flex-col items-center justify-center min-h-[350px] transition-all duration-500 shadow-2xl overflow-hidden py-8 group"
                                    style={{ backgroundColor: themeForm.bgColor }}
                                >
                                    {}
                                    <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500 pointer-events-none z-10"></div>

                                    {themeForm.bgImage && (
                                        <div 
                                            className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-60 mix-blend-overlay" 
                                            style={{ backgroundImage: `url(${themeForm.bgImage})` }} 
                                        />
                                    )}
                                    <div className="relative z-20 w-full max-w-[280px] md:max-w-[320px] bg-white/10 backdrop-blur-xl p-6 md:p-8 rounded-2xl md:rounded-3xl border border-white/20 mx-4 shadow-2xl flex flex-col items-center ring-1 ring-black/5">
                                        <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-4 border border-white/10">
                                            <div className="w-6 h-6 rounded-sm bg-white/50"></div>
                                        </div>
                                        <h4 style={{ color: themeForm.textColor }} className="text-xl md:text-2xl font-bold mb-6 transition-colors duration-500 text-center">Unlock Destination</h4>
                                        <div className="w-full space-y-3 mb-8">
                                            <div className="w-full h-12 bg-black/30 rounded-xl border border-white/5 flex items-center px-4">
                                                <div className="w-5 h-5 rounded-full bg-white/20 mr-3"></div>
                                                <div className="h-2.5 rounded-full bg-white/20 w-32"></div>
                                            </div>
                                            <div className="w-full h-12 bg-black/30 rounded-xl border border-white/5 flex items-center px-4">
                                                <div className="w-5 h-5 rounded-full bg-white/20 mr-3"></div>
                                                <div className="h-2.5 rounded-full bg-white/20 w-24"></div>
                                            </div>
                                        </div>
                                        <button 
                                            disabled
                                            style={{ backgroundColor: themeForm.buttonColor, color: themeForm.textColor }} 
                                            className={`w-full px-6 py-4 rounded-xl text-base font-bold transition-all duration-300 shadow-xl shadow-black/20 hover:scale-105 ${themeForm.animation === 'pulse' ? 'animate-pulse' : themeForm.animation === 'bounce' ? 'animate-bounce' : ''}`}
                                        >
                                            Complete Steps
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-white/10 mt-8">
                            <AnimatePresence>
                                <div className="flex-1">
                                    {themeMsg.text && (
                                        <motion.span 
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0 }}
                                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${themeMsg.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}
                                        >
                                            {themeMsg.type === 'success' ? (
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                            ) : (
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                            )}
                                            {themeMsg.text}
                                        </motion.span>
                                    )}
                                </div>
                            </AnimatePresence>
                            <button
                                type="submit"
                                disabled={themeUpdating}
                                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 px-8 rounded-xl transition-all shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] disabled:opacity-50 flex items-center justify-center gap-2 text-base active:scale-95 group"
                            >
                                {themeUpdating ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Paintbrush className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                                        Save Theme Configuration
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-center bg-black/20 rounded-2xl border border-white/5 border-dashed relative z-10">
                        <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-4 border border-white/10">
                            <Paintbrush className="h-8 w-8 text-slate-500" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">No Gateway Selected</h3>
                        <p className="text-slate-400 max-w-md mx-auto">Please choose a gateway from the dropdown above to start customizing its visual appearance.</p>
                    </div>
                )}
             </div>
          </motion.div>
        )}

        {activeTab === 'profile' && (
          <motion.div 
            key="profile"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="max-w-5xl mx-auto space-y-6"
          >
            <div className="bg-[#0b101e] rounded-3xl border border-white/5 overflow-hidden shadow-2xl relative">
                 {}
                 <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-blue-600/10 to-transparent pointer-events-none" />
                 
                 <div className="p-8 relative z-10">
                     <div className="flex flex-col md:flex-row items-center gap-8 mb-10 border-b border-white/5 pb-10">
                         {}
                         <div className="relative group cursor-pointer flex-shrink-0">
                             {user.photoUrl ? (
                                <img src={user.photoUrl} alt="Profile" className="h-32 w-32 rounded-3xl border border-white/10 shadow-2xl object-cover transition-transform group-hover:scale-105" />
                             ) : (
                                <div className="h-32 w-32 rounded-3xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center border border-white/10 shadow-2xl backdrop-blur-sm transition-transform group-hover:scale-105">
                                   <UserIcon className="h-12 w-12 text-indigo-300" />
                                </div>
                             )}
                             <div className="absolute inset-0 bg-black/60 rounded-3xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm">
                                 <Edit2 className="h-6 w-6 text-white drop-shadow-md" />
                             </div>
                         </div>

                         {}
                         <div className="flex-1 text-center md:text-left">
                             {isEditingName ? (
                                 <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                                     <input
                                        type="text"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        className="bg-black/40 text-white border border-white/10 rounded-xl px-4 py-2 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all font-medium"
                                        placeholder="Enter new name"
                                        autoFocus
                                     />
                                     <button onClick={handleUpdateName} className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl text-sm font-bold transition-colors shadow-lg shadow-indigo-500/20">Save</button>
                                     <button onClick={() => setIsEditingName(false)} className="text-slate-400 hover:text-white px-2 py-2 text-sm font-medium transition-colors">Cancel</button>
                                 </div>
                             ) : (
                                 <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                                     <h2 className="text-3xl font-bold text-white tracking-tight">{user.name}</h2>
                                     <button onClick={() => { setNewName(user.name); setIsEditingName(true); }} className="text-slate-500 hover:text-indigo-400 p-1.5 hover:bg-white/5 rounded-lg transition-colors">
                                         <Edit2 className="h-4 w-4" />
                                     </button>
                                 </div>
                             )}

                             <p className="text-slate-400 font-mono text-sm mb-4">{user.email}</p>
                             
                             <div className="flex items-center justify-center md:justify-start gap-3">
                                 <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${user.plan !== 'free' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' : 'bg-slate-800 text-slate-300 border border-slate-700'}`}>
                                     {user.plan !== 'free' ? <Crown className="h-3.5 w-3.5" /> : null}
                                     {user.plan === 'free' ? 'Free Plan' : `Premium (${user.plan.split('_')[1]})`}
                                 </span>
                             </div>

                             <AnimatePresence>
                             {profileMsg.text && (
                                 <motion.div 
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className={`mt-4 inline-flex items-center gap-2 text-sm px-4 py-2 rounded-xl border ${profileMsg.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'} font-medium`}
                                 >
                                     {profileMsg.type === 'success' ? <Star className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                                     {profileMsg.text}
                                 </motion.div>
                             )}
                             </AnimatePresence>
                         </div>
                     </div>

                     <div className="grid md:grid-cols-2 gap-8">
                         {}
                         <div className="relative group overflow-hidden rounded-2xl bg-black/40 border border-white/5 p-6 hover:border-white/10 transition-colors">
                             <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                             
                             <div className="flex items-center gap-3 mb-6 relative z-10">
                                <div className={`p-2.5 rounded-xl ${user.plan !== 'free' ? 'bg-gradient-to-br from-yellow-500/20 to-amber-600/20 shadow-[0_0_15px_rgba(234,179,8,0.2)]' : 'bg-white/5'}`}>
                                    <Crown className={`h-5 w-5 ${user.plan !== 'free' ? 'text-yellow-400' : 'text-slate-400'}`} />
                                </div>
                                <h3 className="text-lg font-bold text-white">Subscription Plan</h3>
                             </div>
                             
                             <div className="relative z-10">
                                 {user.plan !== 'free' ? (
                                     <div className="space-y-4">
                                        <div>
                                            <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 capitalize tracking-tight mb-1">
                                                {user.plan ? user.plan.replace('_', ' ') : ''}
                                            </div>
                                            {user.premiumUntil && (
                                                <p className="text-sm text-slate-400 flex items-center gap-2">
                                                    <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span> Active until {new Date(user.premiumUntil).toLocaleDateString()}
                                                </p>
                                            )}
                                        </div>
                                        <button onClick={() => navigate('/pricing')} className="w-full bg-white/5 hover:bg-white/10 text-white text-sm font-semibold py-3 rounded-xl transition-colors border border-white/10">
                                            Manage Subscription
                                        </button>
                                     </div>
                                 ) : (
                                     <div className="space-y-4">
                                        <div>
                                            <span className="text-2xl font-bold text-white block tracking-tight mb-1">Free Tier</span>
                                            <p className="text-sm text-slate-400 leading-relaxed">Unlock unlimited links, advanced analytics, and custom themes.</p>
                                        </div>
                                        <button onClick={() => navigate('/pricing')} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-sm font-bold py-3 rounded-xl transition-all shadow-lg shadow-indigo-500/20 active:scale-95 group relative overflow-hidden">
                                            <span className="relative z-10 flex items-center justify-center gap-2">Upgrade Now <Crown className="h-4 w-4" /></span>
                                        </button>
                                     </div>
                                 )}
                             </div>
                         </div>

                         {}
                         <div className="rounded-2xl bg-black/40 border border-white/5 p-6 hover:border-white/10 transition-colors flex flex-col justify-center items-center text-center">
                             <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center border border-white/10 mb-4 shadow-xl">
                                 <KeyRound className="h-8 w-8 text-indigo-400" />
                             </div>
                             <h3 className="text-xl font-bold text-white mb-2">Account Center</h3>
                             <p className="text-sm text-slate-400 mb-6 max-w-xs">Manage your connected accounts, email, and password settings.</p>
                             <button onClick={() => setShowAccountCenterModal(true)} className="w-full max-w-xs bg-white/5 hover:bg-white/10 text-white text-sm font-bold py-3 px-6 rounded-xl transition-all border border-white/10 shadow-lg">
                                 Open Account Center
                             </button>
                         </div>
                     </div>

                     <div className="mt-10 pt-8 border-t border-white/5 flex justify-center md:justify-end">
                         <button
                            onClick={handleLogout}
                            className="flex items-center justify-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 hover:text-red-300 px-6 py-3 rounded-xl font-bold transition-all active:scale-95 w-full md:w-auto"
                         >
                            <LogOut className="h-4 w-4" />
                            Sign Out
                         </button>
                     </div>
                 </div>
            </div>
          </motion.div>
        )}
        </AnimatePresence>
        </div>
      </div>

      {}
      <div className="md:hidden fixed bottom-4 left-4 right-4 z-50">
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl px-2 py-2 flex justify-around items-center shadow-[0_8px_30px_rgb(0,0,0,0.5)]">
           {navItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex flex-col items-center justify-center w-16 h-14 relative z-10 transition-all duration-300 ${isActive ? 'text-white scale-105' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="mobile-nav-pill"
                      className="absolute inset-0 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-xl -z-10 shadow-lg shadow-blue-500/30"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                  <item.icon className={`h-5 w-5 mb-1 transition-all duration-300 ${isActive ? 'drop-shadow-sm' : ''}`} />
                  <span className={`text-[10px] font-bold transition-all duration-300 ${isActive ? 'opacity-100' : 'opacity-70'}`}>{item.label}</span>
                </button>
              )
           })}
        </div>
      </div>
      </div>

      {}
      <AnimatePresence>
        {showAccountCenterModal && (
            <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-md"
            >
                <motion.div 
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    className="bg-gray-900 border border-white/10 rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                >
                    {}
                    <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between sticky top-0 bg-gray-900 z-10">
                        <div className="flex items-center gap-3">
                            <KeyRound className="h-5 w-5 text-indigo-400" />
                            <h3 className="text-lg font-bold text-white tracking-tight">Account Center</h3>
                        </div>
                        <button onClick={() => setShowAccountCenterModal(false)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
                            <Plus className="h-5 w-5 rotate-45" />
                        </button>
                    </div>

                    {}
                    <div className="p-6 space-y-6 overflow-y-auto">
                        
                        {}
                        <AnimatePresence>
                            {accountCenterMsg.text && (
                                <motion.div 
                                    initial={{ opacity: 0, y: -10 }} 
                                    animate={{ opacity: 1, y: 0 }} 
                                    exit={{ opacity: 0, y: -10 }}
                                    className={`p-4 rounded-xl flex items-center gap-3 text-sm font-medium border shadow-lg ${
                                        accountCenterMsg.type === 'error' 
                                        ? 'bg-red-500/10 border-red-500/20 text-red-400' 
                                        : 'bg-green-500/10 border-green-500/20 text-green-400'
                                    }`}
                                >
                                    {accountCenterMsg.type === 'error' ? <AlertCircle className="w-5 h-5 shrink-0" /> : <CheckCircle2 className="w-5 h-5 shrink-0" />}
                                    <span>{accountCenterMsg.text}</span>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {}
                        {user.email && (
                            <div className="space-y-3">
                                <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Security</h4>
                                <div className="group">
                                    <button onClick={() => setIsChangingPassword(!isChangingPassword)} className="w-full bg-white/5 hover:bg-white/10 border border-white/5 rounded-2xl p-4 flex items-center justify-between transition-colors">
                                        <div className="flex items-center gap-4">
                                            <div className="p-2.5 bg-blue-500/10 rounded-xl group-hover:bg-blue-500/20 transition-colors">
                                                <KeyRound className="h-5 w-5 text-blue-400" />
                                            </div>
                                            <div className="text-left">
                                                <span className="text-sm font-bold text-slate-200 block">Change Password</span>
                                                <span className="text-xs text-slate-500">Update your current password</span>
                                            </div>
                                        </div>
                                        <span className="text-xs font-semibold bg-white/5 px-3 py-1.5 rounded-lg text-slate-400 group-hover:text-white transition-colors">Edit</span>
                                    </button>
                                    
                                    <AnimatePresence>
                                    {isChangingPassword && (
                                        <motion.form 
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            onSubmit={handleChangePassword} 
                                            className="mt-3 space-y-3 p-5 bg-black/40 rounded-2xl border border-white/5 overflow-hidden"
                                        >
                                            <input type="password" placeholder="Current Password" required value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-500 transition-colors" />
                                            <input type="password" placeholder="New Password" required value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-500 transition-colors" />
                                            <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold py-3 rounded-xl transition-colors shadow-lg shadow-indigo-500/20 mt-2">Update Password</button>
                                        </motion.form>
                                    )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        )}

                        {}
                        <div className="space-y-3">
                            <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Connected Accounts</h4>
                            
                            {}
                            <div className="bg-white/5 border border-white/5 rounded-2xl p-4 flex items-center justify-between hover:bg-white/[0.07] transition-colors">
                                <div className="flex items-center gap-4">
                                    <div className="p-2.5 bg-[#0088cc]/10 rounded-xl">
                                        <Send className="h-5 w-5 text-[#0088cc]" />
                                    </div>
                                    <div className="text-left">
                                        <span className="text-sm font-bold text-slate-200 block">Telegram App</span>
                                        <span className="text-xs text-slate-500">Fast login & notifications</span>
                                    </div>
                                </div>
                                {user.telegramId ? (
                                    <button onClick={() => { setShowAccountCenterModal(false); setShowDisconnectModal(true); }} className="text-xs font-bold text-red-400 hover:text-white bg-red-500/10 hover:bg-red-500 px-3 py-1.5 rounded-lg transition-colors border border-red-500/20 hover:border-transparent">Disconnect</button>
                                ) : (
                                    <div className="flex justify-center items-center py-0.5 px-3 bg-[#0088cc]/10 hover:bg-[#0088cc]/20 rounded-lg transition-colors border border-[#0088cc]/20 overflow-hidden scale-[0.8] origin-right h-[32px]">
                                        <LoginButton botUsername={process.env.REACT_APP_TELEGRAM_BOT_NAME || "OfficialLinkeyBot"} onAuthCallback={handleConnectTelegram} buttonSize="small" />
                                    </div>
                                )}
                            </div>

                            {}
                            <div className="group">
                                <div className="bg-white/5 border border-white/5 rounded-2xl p-4 flex items-center justify-between transition-colors">
                                    <div className="flex items-center gap-4">
                                        <div className="p-2.5 bg-indigo-500/10 rounded-xl group-hover:bg-indigo-500/20 transition-colors">
                                            <Mail className="h-5 w-5 text-indigo-400" />
                                        </div>
                                        <div className="text-left">
                                            <span className="text-sm font-bold text-slate-200 block">Email Login</span>
                                            {user.email ? (
                                                <span className="text-xs font-medium text-slate-400 block">{user.email}</span>
                                            ) : (
                                                <span className="text-xs text-slate-500 block">Use email to sign in</span>
                                            )}
                                        </div>
                                    </div>
                                    {!user.email && (
                                        <button onClick={() => setIsConnectingEmail(!isConnectingEmail)} className="text-xs font-semibold bg-white/5 px-3 py-1.5 rounded-lg text-slate-400 group-hover:text-white group-hover:bg-white/10 transition-colors">Connect</button>
                                    )}
                                </div>
                                
                                <AnimatePresence>
                                {isConnectingEmail && !user.email && (
                                    <motion.div 
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="mt-3 space-y-4 p-5 bg-black/40 rounded-2xl border border-white/5 overflow-hidden"
                                    >
                                        {!showConnectOtpInput ? (
                                            <div className="space-y-3">
                                                <input type="email" placeholder="Email Address" required value={connectEmail} onChange={e => setConnectEmail(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-500 transition-colors" />
                                                <button onClick={handleConnectEmailRequest} type="button" className="w-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold py-3 rounded-xl transition-colors shadow-lg shadow-indigo-500/20 mt-2">Send OTP</button>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                <div className="text-xs text-slate-400 font-medium bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-3 flex items-start gap-2">
                                                    <Mail className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
                                                    <span>We sent a verification code to <span className="text-white font-bold">{connectEmail}</span></span>
                                                </div>
                                                
                                                <div className="space-y-3">
                                                    <div className="relative">
                                                        <input type="text" placeholder="Enter OTP" required value={connectOtp} onChange={e => setConnectOtp(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl pl-4 pr-12 py-3 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-500 tracking-[0.5em] font-bold transition-colors" maxLength={6} />
                                                        <button 
                                                            type="button"
                                                            onClick={async () => {
                                                                const tg = window.Telegram?.WebApp;
                                                                if (tg && tg.readTextFromClipboard) {
                                                                    tg.readTextFromClipboard((text) => {
                                                                        if (text) setConnectOtp(text.trim().substring(0, 6));
                                                                    });
                                                                } else {
                                                                    try {
                                                                        const text = await navigator.clipboard.readText();
                                                                        if (text) setConnectOtp(text.trim().substring(0, 6));
                                                                    } catch (err) {
                                                                        console.error('Failed to read clipboard', err);
                                                                        setAccountCenterMsg({ text: 'Please allow clipboard permissions', type: 'error' });
                                                                        setTimeout(() => setAccountCenterMsg({ text: '', type: '' }), 3000);
                                                                    }
                                                                }
                                                            }}
                                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                                            title="Paste OTP"
                                                        >
                                                            <ClipboardPaste className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                    <input type="password" placeholder="Set a Password" required value={connectPassword} onChange={e => setConnectPassword(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-500 transition-colors" />
                                                    
                                                    <div className="flex gap-2 pt-2">
                                                        <button onClick={() => setShowConnectOtpInput(false)} type="button" className="px-4 py-3 rounded-xl border border-white/10 text-slate-400 hover:bg-white/5 hover:text-white text-sm font-bold transition-colors">Back</button>
                                                        <button onClick={handleConnectEmailVerify} type="button" className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold py-3 rounded-xl transition-colors shadow-lg shadow-indigo-500/20">Verify & Connect</button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                                </AnimatePresence>
                            </div>
                        </div>

                    </div>
                </motion.div>
            </motion.div>
        )}
      </AnimatePresence>

      {}
      <AnimatePresence>
        {showDisconnectModal && (
            <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            >
                <motion.div 
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-gray-900 border border-gray-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl relative overflow-hidden"
                >
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-orange-500"></div>
                    <div className="flex flex-col items-center text-center mb-6">
                        <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4 border border-red-500/20">
                            <AlertCircle className="w-6 h-6 text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Are you sure?</h3>
                        <p className="text-gray-400 text-sm">
                            This will delete everything, your account data and links generated. Are you sure you want to continue?
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-3 w-full">
                        <button 
                            onClick={() => setShowDisconnectModal(false)}
                            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-700 hover:bg-gray-800 text-white font-medium transition-colors"
                        >
                            No
                        </button>
                        <button 
                            onClick={handleDisconnectTelegram}
                            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold transition-colors shadow-lg shadow-red-500/25"
                        >
                            Delete
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Dashboard;
